import type {
  ChatAppResponse,
  ChatAppResponseOrError,
  Document,
  DocumentReferencePreview,
  LegacyApiResponse,
  ResponseContext,
  ResponseContextDataPoint,
  Thoughts,
} from "@/api/models"

/**
 * Checks if the response is in the legacy format with choices
 */
export function isLegacyFormat(response: ChatAppResponseOrError | LegacyApiResponse): boolean {
  return (
    !("error" in response) &&
    "choices" in response &&
    Array.isArray(response.choices) &&
    response.choices.length > 0
  )
}

/**
 * Safely gets the response content from either new or legacy format
 */
export function getResponseContent(response: ChatAppResponseOrError | LegacyApiResponse): string {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!response) {
    return ""
  }

  if ("error" in response && response.error) {
    return ""
  }

  // New format
  if ("content" in response && typeof response.content === "string") {
    return response.content
  }

  // Legacy format
  if (
    "choices" in response &&
    Array.isArray(response.choices) &&
    response.choices.length > 0 &&
    response.choices[0]?.message?.content
  ) {
    return response.choices[0].message.content
  }

  return ""
}

/**
 * Filenames a past assistant turn delivered as attachments.
 *
 * Read the same two places `system-message.tsx` reads to draw the attachment
 * card: the top-level field first, the response context second (streaming
 * updates land there). Used when re-sending history so a hosted agent knows
 * what it has already delivered — without it the agent answers "what was the
 * report called?" with "some report file was delivered", because the tool
 * exchange that produced the file belongs to a turn that is not replayed.
 */
export function getAssistantFiles(
  response: ChatAppResponseOrError | LegacyApiResponse | undefined
): string[] {
  if (!response || ("error" in response && response.error)) {
    return []
  }
  const holder = response as {
    assistant_files?: string[]
    context?: { assistant_files?: string[] }
  }
  const files = holder.assistant_files ?? holder.context?.assistant_files
  if (!Array.isArray(files)) {
    return []
  }
  return files.filter((name): name is string => typeof name === "string" && name.length > 0)
}

/**
 * Safely gets the context object from the response
 */
export function getResponseContext(
  response: ChatAppResponseOrError | LegacyApiResponse
): ResponseContext {
  const defaultContext: ResponseContext = {
    data_points: [],
    followup_questions: null,
    thoughts: [],
  }

  if ("error" in response && response.error) {
    return defaultContext
  }

  // New format
  if ("context" in response && response.context) {
    const context = response.context as ResponseContext
    return context
  }

  // Legacy format
  if ("choices" in response && Array.isArray(response.choices) && response.choices.length > 0) {
    return response.choices[0].context as ResponseContext
  }

  return defaultContext
}

/**
 * Safely gets the session state from the response
 */
export function getSessionState(
  response: ChatAppResponseOrError | LegacyApiResponse
): string | null {
  if ("error" in response && response.error) {
    return null
  }

  // New format
  if ("session_state" in response) {
    return response.session_state || null
  }

  // Legacy format
  if ("choices" in response && Array.isArray(response.choices) && response.choices.length > 0) {
    return response.choices[0]?.session_state || null
  }

  return null
}

/**
 * Gets data points from the response context
 */
export function getDataPoints(
  response: ChatAppResponseOrError | LegacyApiResponse
): string[] | ResponseContextDataPoint[] | { text: string[]; images?: { url: string }[] } {
  const context = getResponseContext(response)
  return context.data_points
}

/**
 * Checks if the response has data points
 */
export function hasDataPoints(response: ChatAppResponseOrError | LegacyApiResponse): boolean {
  const dataPoints = getDataPoints(response)
  return Array.isArray(dataPoints) ? dataPoints.length > 0 : !!dataPoints
}

/**
 * Gets thoughts from the response context
 */
export function getThoughts(response: ChatAppResponseOrError | LegacyApiResponse): Thoughts[] {
  const context = getResponseContext(response)
  return context.thoughts
}

/**
 * Checks if the response has thoughts
 */
export function hasThoughts(response: ChatAppResponseOrError | LegacyApiResponse): boolean {
  const thoughts = getThoughts(response)
  return Array.isArray(thoughts) && thoughts.length > 0
}

/**
 * Gets followup questions from the response context
 */
export function getFollowupQuestions(
  response: ChatAppResponseOrError | LegacyApiResponse
): string[] | null {
  const context = getResponseContext(response)
  return context.followup_questions
}

/**
 * Gets documents from the response context
 */
export function getDocuments(
  response: ChatAppResponseOrError | LegacyApiResponse
): [string, Document][] | undefined {
  const context = getResponseContext(response)
  return context.documents
}

/**
 * Gets standalone document preview references from the response context
 */
export function getDocumentReferences(
  response: ChatAppResponseOrError | LegacyApiResponse
): DocumentReferencePreview[] | undefined {
  const context = getResponseContext(response)
  return context.document_references
}

/**
 * Converts a legacy format response to the new format
 */
export function convertToNewFormat(legacyResponse: LegacyApiResponse): ChatAppResponse {
  const defaultContext: ResponseContext = {
    data_points: [],
    followup_questions: null,
    thoughts: [],
  }

  if (
    "choices" in legacyResponse &&
    Array.isArray(legacyResponse.choices) &&
    legacyResponse.choices.length > 0
  ) {
    const choice = legacyResponse.choices[0]

    return {
      content: choice.message.content || "",
      context: choice.context as ResponseContext,
      session_state: choice.session_state || null,
      completed: false,
    }
  }

  return {
    content: "",
    context: defaultContext,
    session_state: null,
    completed: false,
  }
}
